import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FocusSearchQuestion } from '../types/sparkTypes';
import styles from '../styles/FSQCard.module.css';

interface FSQCardProps {
  fsq: FocusSearchQuestion;
  index: number;
  isSelected: boolean;
  onToggleSelect: (index: number, isSelected: boolean) => void;
  onUpdateFSQ: (index: number, updated: FocusSearchQuestion) => void;
  isDisabled?: boolean;
  isNewCard?: boolean;
}

/**
 * Card component for displaying Focus Search Questions with selection checkbox
 * and inline editing of title and question text.
 */
const FSQCard: React.FC<FSQCardProps> = ({
  fsq,
  index,
  isSelected,
  onToggleSelect,
  onUpdateFSQ,
  isDisabled = false,
  isNewCard = false
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(isNewCard);
  const [isEditingQuestion, setIsEditingQuestion] = useState(false);
  const [draftTitle, setDraftTitle] = useState(fsq.title);
  const [draftQuestion, setDraftQuestion] = useState(fsq.question);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Sync drafts when props change (e.g. FSQs regenerated)
  useEffect(() => {
    setDraftTitle(fsq.title);
    setDraftQuestion(fsq.question);
  }, [fsq.title, fsq.question]);

  // Auto-focus title input for new cards
  useEffect(() => {
    if (isNewCard && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isNewCard]);

  const handleCardClick = () => {
    if (!isDisabled) {
      onToggleSelect(index, !isSelected);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (!isDisabled) {
      onToggleSelect(index, e.target.checked);
    }
  };

  // --- Title editing ---
  const handleTitleClick = (e: React.MouseEvent) => {
    if (isDisabled) return;
    e.stopPropagation();
    if (!isEditingTitle) {
      setIsEditingTitle(true);
      setDraftTitle(fsq.title);
    }
  };

  const commitTitle = useCallback(() => {
    const trimmed = draftTitle.trim();
    if (trimmed && trimmed !== fsq.title) {
      onUpdateFSQ(index, { ...fsq, title: trimmed });
    } else {
      setDraftTitle(fsq.title);
    }
    setIsEditingTitle(false);
  }, [draftTitle, fsq, index, onUpdateFSQ]);

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitTitle();
    } else if (e.key === 'Escape') {
      setDraftTitle(fsq.title);
      setIsEditingTitle(false);
    }
  };

  // --- Question editing ---
  const handleQuestionClick = (e: React.MouseEvent) => {
    if (isDisabled) return;
    e.stopPropagation();
    if (!isEditingQuestion) {
      setIsEditingQuestion(true);
      setDraftQuestion(fsq.question);
    }
  };

  const commitQuestion = useCallback(() => {
    const trimmed = draftQuestion.trim();
    if (trimmed && trimmed !== fsq.question) {
      onUpdateFSQ(index, { ...fsq, question: trimmed });
    } else {
      setDraftQuestion(fsq.question);
    }
    setIsEditingQuestion(false);
  }, [draftQuestion, fsq, index, onUpdateFSQ]);

  const handleQuestionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commitQuestion();
    } else if (e.key === 'Escape') {
      setDraftQuestion(fsq.question);
      setIsEditingQuestion(false);
    }
  };

  return (
    <li
      className={`${styles.cardWrapper} ${isSelected ? styles.selected : ''} ${isDisabled ? styles.disabled : ''}`}
      onClick={handleCardClick}
      title={isDisabled ? 'Processing...' : `Select "${fsq.title}"`}
    >
      {/* Selection overlay with checkbox */}
      <div className={styles.selectionOverlay}>
        <input
          type="checkbox"
          className={styles.selectionCheckbox}
          checked={isSelected}
          onChange={handleCheckboxChange}
          onClick={(e) => e.stopPropagation()}
          disabled={isDisabled}
        />
      </div>

      {/* Card content */}
      <div className={styles.cardContent}>
        {isEditingTitle ? (
          <input
            ref={titleInputRef}
            className={styles.titleInput}
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={handleTitleKeyDown}
            onClick={(e) => e.stopPropagation()}
            placeholder="Question title"
            autoFocus
          />
        ) : (
          <h3
            className={`${styles.title} ${!isDisabled ? styles.editable : ''}`}
            onClick={handleTitleClick}
          >
            {fsq.title || 'Click to add title'}
          </h3>
        )}

        {isEditingQuestion ? (
          <textarea
            className={styles.questionInput}
            value={draftQuestion}
            onChange={(e) => setDraftQuestion(e.target.value)}
            onBlur={commitQuestion}
            onKeyDown={handleQuestionKeyDown}
            onClick={(e) => e.stopPropagation()}
            placeholder="Question text"
            autoFocus
          />
        ) : (
          <p
            className={`${styles.question} ${!isDisabled ? styles.editable : ''}`}
            onClick={handleQuestionClick}
          >
            {fsq.question || 'Click to add question'}
          </p>
        )}
      </div>
    </li>
  );
};

export default FSQCard;
